import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { isEmpty } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { UserRoleType, UserRoleTypeUpperCase } from '@vi-iot/client';

import { IAMRoles, UserDetailsUserRole } from '@store';
import { AuthStore, InstallationsMigrationStore, UserDetailsStore } from '@store';

import { PillType } from '../../shared/components/pill/pill.model';
import { InstallationsMigrationService } from '../../+installations/installations/installation-migration/installations-migration-service';
import { SupportedCountry } from '../../shared/models/supportedCountry.model';
import { SupportedCountriesService } from '../../shared/services/supported-countries.service';
import { matTooltipSetter } from '../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';
import {
  ViBreadcrumbsComponent,
  ViContentComponent,
  ViHeaderComponent,
  ViIconButtonComponent,
  ViItemComponent,
  ViLoaderComponent,
  ViPageComponent,
  ViPanelComponent,
  ViToolbarComponent,
} from '@vi-iot/ui';
import { PillComponent, SidenavComponent } from '@components';
import {
  CountryPipe,
  DashPipe,
  FormatPhoneNumberPipe,
  GenderPipe,
  LanguagePipe,
  NO_VALUE,
} from '@pipes';
import { BooleanSuffixPipe } from '../../shared/pipes/boolean-suffix/boolean-suffix.pipe';
import { MatIconModule } from '@angular/material/icon';
import { RegionPipe } from '../../shared/pipes/region/region.pipe';
import { UserGatewayListComponent } from './user-gateway-list/user-gateway-list.component';
import { UserBookingListComponent } from './user-booking-list/user-booking-list.component';
import { LetDirective } from '@ngrx/component';
import { MatButtonModule } from '@angular/material/button';
import { ChangeUserDetailsDirective } from './change-user-details.directive';
import { ViDswFooterComponent } from '../../shared/+design-system-web/vi-dsw-footer/vi-dsw-footer.component';

interface Me {
  companyCountryCode: string;
  companyId: string;
  companyName: string;
  vkorg: string;
  email: string;
  familyName: string;
  firstName: string;
  iotRole: UserRoleType;
  phone: any;
  roles: string[];
}

export interface User {
  firstName: string;
  lastName: string;
  fullName: string;
  gender: string;
  customerNumber: any;
}

@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    ViPageComponent,
    ViToolbarComponent,
    ViIconButtonComponent,
    ViBreadcrumbsComponent,
    ViHeaderComponent,
    ViContentComponent,
    ViLoaderComponent,
    ViPanelComponent,
    PillComponent,
    ViItemComponent,
    DashPipe,
    BooleanSuffixPipe,
    GenderPipe,
    MatTooltipModule,
    MatIconModule,
    CountryPipe,
    RegionPipe,
    FormatPhoneNumberPipe,
    LanguagePipe,
    UserGatewayListComponent,
    UserBookingListComponent,
    SidenavComponent,
    LetDirective,
    MatButtonModule,
    ChangeUserDetailsDirective,
    ViDswFooterComponent,
    RouterModule,
  ],
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  readonly IAMRoles = IAMRoles;
  readonly pillType = PillType;
  readonly NO_VALUE = NO_VALUE;

  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  viIntRole = IAMRoles.viInt;
  viExtRole = IAMRoles.viExt;

  userId: string;
  userRole: UserDetailsUserRole;
  userRoles$: Observable<string[]> = this.userDetailsStore.userRoles$;
  user$ = this.userDetailsStore.getUser();
  isActive$ = this.userDetailsStore.isUserActive();
  isEmailVerified$ = this.userDetailsStore.isEmailVerified();
  userContact$ = this.userDetailsStore.getUserContact();
  userAddress$ = this.userDetailsStore.getUserAddress();
  gatewaysVM$ = this.userDetailsStore.getUserGateways();
  userHasAccessToInvitations$ = this.userDetailsStore.userHasAccessToInvitations();
  companyName$ = this.userDetailsStore.companyName$;
  me: Me;
  routeData$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  ngUnsubscribe: Subject<void> = new Subject<void>();
  supportedCountries: SupportedCountry[];

  isEditButtonDisabled: boolean;

  constructor(
    public userDetailsStore: UserDetailsStore,
    public authStore: AuthStore,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private installationsMigrationService: InstallationsMigrationService,
    private installationsMigrationStore: InstallationsMigrationStore,
    private supportedCountriesService: SupportedCountriesService
  ) {}

  ngOnInit(): void {
    this.supportedCountries = this.supportedCountriesService.getUniqueSupportedCountriesSortedAlphabetically();

    combineLatest([this.authStore.me$, this.userDetailsStore.getUserRole()])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([me, userRole]) => {
        this.me = me;
        this.userRole = userRole;

        if (this.me && this.userRole) {
          this.isEditButtonDisabled = this.checkIfEditButtonIsDisabled(userRole.role, me.iotRole);
        }
      });

    this.activatedRoute.params.subscribe((param) => {
      this.userId = param['id'];
      this.userDetailsStore.getUserDetails(this.userId);
    });

    this.activatedRoute.data.subscribe((data) => {
      this.routeData$.next(!isEmpty(data) ? data : null);
    });

    this.user$
      .pipe(
        filter((user) => !!user.customerNumber),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((value) => {
        this.userDetailsStore.getUserCompany(value.customerNumber);
      });
  }

  canAccessInstallationsMigration(): boolean {
    return this.installationsMigrationService.isUserPermittedToMigrateInstallation();
  }

  onResetPassword(loginId: string): void {
    this.userDetailsStore.requestPasswordReset(loginId);
  }

  stopEvent(event: MouseEvent): void {
    event.preventDefault();
  }

  onResendConfirmationEmail(loginId: string): void {
    this.userDetailsStore.resendConfirmationEmail(loginId);
  }

  disableResendEmail(meRsaRole: string): boolean {
    return meRsaRole === UserRoleType.supportLocalReadOnly;
  }

  private checkIfEditButtonIsDisabled(role: string, meRsaRole: string): boolean {
    // depending on API version v1/v2 the name can start with a different capitalization
    const isUserEK = [
      UserRoleTypeUpperCase.endUser.toLowerCase(),
      UserRoleTypeUpperCase.endUserWithoutIam.toLowerCase(),
    ].includes(role.toLowerCase() as UserRoleTypeUpperCase);

    // we can only edit user details if:
    // edited user is end user (we shouldn't edit maintainers - that should be done in salesforce)
    // loggedIn user has either supportInternational or admin role
    return (
      !isUserEK ||
      meRsaRole === UserRoleType.supportLocalReadOnly ||
      meRsaRole === UserRoleType.supportLocalReadWrite ||
      meRsaRole === UserRoleType.supportLocalReadWriteWithRemoteDiagnosis ||
      meRsaRole === UserRoleType.supportLocalDepartmentManagement
    );
  }

  getNavigateTo(installationId: number): string {
    return installationId ? `/installations/${installationId}` : '/search';
  }

  back(): void {
    const installationId = this.routeData$.getValue()
      ? this.routeData$.getValue().installationId
      : undefined;
    this.router.navigate([this.getNavigateTo(installationId)]);
  }

  openCustomerMigrationModal(customerNumber: string): void {
    this.installationsMigrationStore.setCustomerNumber(customerNumber);
    this.installationsMigrationService.openMigrationModal();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
